import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { navigate, useLocation } from '@reach/router';
import './DashboardSidebar.scss';
import env from '@/env';
import { getSubdomain, getWebAppInfo } from '@/utils';
import { Paths } from '@/pages/routers';
import { RootState } from '@/redux/reducers';
import IconBurgerMenu from '@/assets/icons/burger-menu.svg';

interface DashboardSidebarProps {
  slim: boolean;
  menus: {
    icon: string;
    iconActive: string;
    label: string;
    to: string;
    requiredRoles: string[];
    absolutePath: boolean;
  }[];
  onToggle: Function;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ slim, menus, onToggle }) => {
  const webApp = getWebAppInfo(env.siteId);
  const { pathname } = useLocation();
  const entity = useSelector((state: RootState) => state.serviceDirectoryEntity.entityDashboard);
  const authUser = useSelector((state: RootState) => state.authUser.data);
  const [activeMenuLabel, setActiveMenuLabel] = useState<string>(menus[0].label);
  const subdomain = getSubdomain();
  const hasSubdomain = subdomain && subdomain !== 'www';

  const handleClickMenu = (menuLabel: string, to: string, absolutePath: boolean) => {
    setActiveMenuLabel(menuLabel);
    absolutePath ? navigate(to) : navigate(`${Paths.Dashboard}/${to}`);
  };

  const handleClickBurgerMenu = () => {
    onToggle && onToggle();
  };

  const canAccess = (requiredRoles: string[], userRoles?: string[]) => {
    if (userRoles) {
      const checkRoles = userRoles.map((role) => requiredRoles.includes(role));
      const hasPermission = checkRoles.includes(true) || !requiredRoles.length;
      return hasPermission;
    }
    return true;
  };

  useEffect(() => {
    const menuPath = pathname.split('/')[2];
    const activeMenu = menus.find((menu) => menu.to === menuPath);
    setActiveMenuLabel(activeMenu ? activeMenu.label : menus[0].label);
  }, [pathname, menus]);

  return (
    <div className={`DashboardSidebar ${slim && 'slim'}`}>
      <div className={`DashboardSidebar-logo ${!entity?.logo && 'apiCafeLogo'}`}>
        <div className="DashboardSidebar-logo-main">
          {!slim && (
            <a href={hasSubdomain ? `${env.rootUrl}${Paths.Entity}/${subdomain}` : '/'}>
              <img src={entity?.logo || webApp.mainLogo} alt={entity?.displayName || webApp.siteName} />
            </a>
          )}
        </div>
        <div className="DashboardSidebar-logo-icon">
          <img src={IconBurgerMenu} alt="Open Banking" onClick={handleClickBurgerMenu} />
        </div>
      </div>

      <div className="DashboardSidebar-menus">
        {menus.map(
          ({ icon, iconActive, label, to, requiredRoles, absolutePath }) =>
            canAccess(requiredRoles, authUser?.roles) && (
              <div
                key={label}
                className={`DashboardSidebar-menu ${activeMenuLabel === label && 'active'}`}
                onClick={() => handleClickMenu(label, to, absolutePath)}
              >
                <img
                  className="DashboardSidebar-menu-icon"
                  src={activeMenuLabel === label ? iconActive : icon}
                  alt="Menu Item"
                />
                <span className="DashboardSidebar-menu-label">{label}</span>
              </div>
            )
        )}
      </div>
    </div>
  );
};

export default DashboardSidebar;
